import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const WS_BASE_DELAY = 2000;      // first retry after 2s
const WS_MAX_DELAY = 30000;      // cap backoff at 30s
const WS_CONNECT_TIMEOUT = 8000; // give the handshake 8s

export function useWebSocket(onMessage, autoConnect = true) {
  const ws = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const connectTimeout = useRef(null);
  const closingRef = useRef(false);
  const connectRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (connectTimeout.current) {
      clearTimeout(connectTimeout.current);
      connectTimeout.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (closingRef.current) return;
    // Exponential backoff with jitter, capped — silent, no console spam.
    const attempt = reconnectAttempts.current;
    const delay = Math.min(WS_BASE_DELAY * 2 ** attempt, WS_MAX_DELAY);
    const jitter = Math.random() * 1000;
    reconnectAttempts.current = attempt + 1;
    reconnectTimer.current = setTimeout(() => {
      connectRef.current && connectRef.current();
    }, delay + jitter);
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setConnectionError('No authentication token');
      return;
    }
    // Don't open a second socket if one is already active/connecting.
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    closingRef.current = false;

    // Route the WebSocket through the backend ingress path (/api). Derive the
    // host from REACT_APP_BACKEND_URL so it works in preview and production.
    let backendHost = window.location.host;
    let backendSecure = window.location.protocol === 'https:';
    try {
      const backendUrl = new URL(process.env.REACT_APP_BACKEND_URL);
      backendHost = backendUrl.host;
      backendSecure = backendUrl.protocol === 'https:';
    } catch (e) { /* fall back to current origin */ }
    const protocol = backendSecure ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${backendHost}/api/ws/notifications?token=${token}`;

    let socket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (e) {
      setConnectionError('Unable to open connection');
      scheduleReconnect();
      return;
    }
    ws.current = socket;

    connectTimeout.current = setTimeout(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        try { socket.close(); } catch (e) { /* noop */ }
      }
    }, WS_CONNECT_TIMEOUT);

    socket.onopen = () => {
      if (connectTimeout.current) { clearTimeout(connectTimeout.current); connectTimeout.current = null; }
      reconnectAttempts.current = 0;
      setIsConnected(true);
      setConnectionError(null);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected' || data.type === 'pong') return;
        if (onMessage) onMessage(data);
      } catch (e) {
        /* ignore malformed frames */
      }
    };

    socket.onclose = () => {
      if (connectTimeout.current) { clearTimeout(connectTimeout.current); connectTimeout.current = null; }
      setIsConnected(false);
      if (ws.current === socket) ws.current = null;
      if (!closingRef.current) scheduleReconnect();
    };

    // Keep the handler quiet — the browser already surfaces WS errors, and
    // onclose drives the reconnect. Logging here caused the console spam.
    socket.onerror = () => {};
  }, [onMessage, scheduleReconnect]);

  connectRef.current = connect;

  const disconnect = useCallback(() => {
    closingRef.current = true;
    clearTimers();
    reconnectAttempts.current = 0;
    if (ws.current) {
      try { ws.current.close(1000); } catch (e) { /* noop */ }
      ws.current = null;
    }
    setIsConnected(false);
  }, [clearTimers]);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  // Periodic ping to keep the connection alive through idle-timeout proxies.
  useEffect(() => {
    if (!isConnected) return;
    const pingInterval = setInterval(() => {
      sendMessage({ type: 'ping' });
    }, 25000);
    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  // Auto-connect on mount.
  useEffect(() => {
    if (autoConnect) connect();
    return () => { disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    sendMessage,
  };
}

// Hook specifically for admin notifications
export function useAdminNotifications() {
  const [notifications, setNotifications] = useState([]);

  const handleMessage = useCallback((data) => {
    if (data.type === 'new_kyc_application') {
      const { applicant_name, customer_type, risk_level } = data.data;
      
      // Add to notifications list
      setNotifications(prev => [{
        id: Date.now(),
        type: 'kyc',
        title: 'New KYC Application',
        message: `${applicant_name} submitted a ${customer_type} application`,
        risk_level,
        timestamp: data.timestamp,
        read: false
      }, ...prev].slice(0, 50));

      // Show toast notification
      toast.info(`New KYC Application: ${applicant_name}`, {
        description: `${customer_type} account - Risk: ${risk_level?.toUpperCase() || 'Pending'}`,
        action: {
          label: 'Review',
          onClick: () => window.location.href = '/admin?tab=kyc'
        }
      });
    } 
    else if (data.type === 'kyc_status_change') {
      const { kyc_id, new_status } = data.data;
      
      setNotifications(prev => [{
        id: Date.now(),
        type: 'kyc_update',
        title: 'KYC Status Changed',
        message: `Application ${kyc_id.substring(0, 8)}... is now ${new_status.replace(/_/g, ' ')}`,
        timestamp: data.timestamp,
        read: false
      }, ...prev].slice(0, 50));
    }
    else if (data.type === 'new_withdrawal') {
      const { amount, currency, type } = data.data;
      
      setNotifications(prev => [{
        id: Date.now(),
        type: 'withdrawal',
        title: 'New Withdrawal Request',
        message: `${amount} ${currency} via ${type}`,
        timestamp: data.timestamp,
        read: false
      }, ...prev].slice(0, 50));

      toast.info('New Withdrawal Request', {
        description: `${amount} ${currency} via ${type}`,
        action: {
          label: 'Review',
          onClick: () => window.location.href = '/admin?tab=withdrawals'
        }
      });
    }
  }, []);

  const websocket = useWebSocket(handleMessage);

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    ...websocket,
    notifications,
    unreadCount,
    markAsRead,
    clearNotifications
  };
}
